
import { Vue, Component, Watch } from 'vue-property-decorator'
import Survey from '@/types/survey'
import User from '@/types/user'
import Column, { FilterOption } from '@/types/column'
import TableSettings from '@/types/tableSettings'
import ActiveFilters from '@/components/listview/ActiveFilters.vue'
import Pagination from '@/types/pagination'
import axios from 'axios'
import SurveyBulkUpdater from '@/components/listview/SurveyBulkUpdater.vue'
import PageHeader from '@/components/base/PageHeader.vue'
import { notification } from 'ant-design-vue'
import SearchableColumnFilter from '@/components/base/SearchableColumnFilter.vue'
import ls from '@/ls'
import { can } from '@/globalFunctions'
import SurveyBulkCreator from '@/components/listview/SurveyBulkCreator.vue'

@Component({
  methods: { can },
  components: { SurveyBulkCreator, SearchableColumnFilter, PageHeader, SurveyBulkUpdater, ActiveFilters }
})

export default class Surveys extends Vue {
  settings = {
    routeNames: { create: 'surveyCreate', show: 'surveyShow', edit: 'surveyEdit' },
    routeParamKey: 'surveyId',
    permissionNames: { show: 'surveys.view', edit: 'surveys.edit', delete: 'surveys.delete' },
    functionNames: { getList: 'getSurveyList' },
    getterNames: { list: 'surveyList', loading: 'loadingSurvey' },
    localStorageName: 'surveyTableSettings'
  }

  columns: Column[] = [
    {
      title: '#',
      key: 'id',
      dataIndex: 'id',
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Project',
      key: 'project.id',
      dataIndex: 'project',
      scopedSlots: { customRender: 'project', filterDropdown: 'customFilter' },
      onFilterDropdownVisibleChange: (visible: boolean): void => this.filterDropdownVisibleChange(visible),
      filters: this.filters?.projects || [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Type',
      key: 'survey_form.type',
      dataIndex: 'type_label',
      filters: [
        {
          text: 'Controle',
          value: 1
        },
        {
          text: 'Inspectie',
          value: 2
        },
        {
          text: 'Keuring',
          value: 3
        },
        {
          text: 'Oplevering',
          value: 4
        },
        {
          text: 'Bewijslast',
          value: 5
        },
        {
          text: 'Voorschouw',
          value: 6
        },
        {
          text: 'Keuringsdocument',
          value: 7
        }
      ],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Formulier',
      key: 'survey_form.id',
      dataIndex: 'survey_form',
      scopedSlots: { customRender: 'survey_form' },
      filters: this.filters?.survey_forms || [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Bouwblok',
      key: 'building_block.name',
      dataIndex: 'building_block.name',
      filters: this.filters?.building_blocks || [],
      scopedSlots: { filterDropdown: 'customFilter' },
      onFilterDropdownVisibleChange: (visible: boolean): void => this.filterDropdownVisibleChange(visible),
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Bouwnummer',
      key: 'building.number',
      dataIndex: 'building.number',
      filters: this.filters?.building_numbers || [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Adres',
      key: 'building.address',
      dataIndex: 'building',
      scopedSlots: { customRender: 'building.address' }
    },
    {
      title: 'Ingepland voor',
      key: 'scheduled_user.id',
      dataIndex: 'scheduled_user.full_name',
      filters: this.filters?.scheduled_users || [],
      scopedSlots: { filterDropdown: 'customFilter' },
      onFilterDropdownVisibleChange: (visible: boolean): void => this.filterDropdownVisibleChange(visible),
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Ingepland op',
      key: 'scheduled_at',
      dataIndex: 'scheduled_at',
      scopedSlots: { customRender: 'scheduled_at' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Voltooid op',
      key: 'completed_at',
      dataIndex: 'completed_at',
      scopedSlots: { customRender: 'completed_at' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status_label',
      filters: this.filters?.statuses || [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Resultaat',
      key: 'result',
      dataIndex: 'result',
      scopedSlots: { customRender: 'result' },
      filters: this.filters?.results || [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Actie\'s',
      key: 'action',
      scopedSlots: { customRender: 'action' }
    }
  ]

  tableSettings:TableSettings = ls.get(this.settings.localStorageName) || {
    pagination: { current: 1, pageSize: 25 },
    showSizeChanger: true,
    pageSizeOptions: ['25', '50', '100'],
    activeFilters: null,
    sort: null,
    search: null
  }

  selectedRowKeys:number[] = []
  downloading = false

  mounted () : void {
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
    this.onTableSettingsChange()
  }

  reloadList () : void {
    this.selectedRowKeys = []
    this.reload()
  }

  reload () :void {
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  destroySurvey (id:number) : void {
    axios.delete(process.env.VUE_APP_API_URL + '/surveys/' + id).then(() => {
      notification.success({ message: 'Keuring verwijderd.', description: 'De keuring is succesvol verwijderd.', duration: 3 })
      this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
    }).catch(() => {
      notification.error({ message: 'Keuring NIET verwijderd.', description: 'De keuring is NIET verwijderd. Probeer het later nog eens.', duration: 0 })
    })
  }

  downloadExcel () : void {
    this.downloading = true

    axios.post(process.env.VUE_APP_API_URL + '/surveys/download/excel', {
      filters: this.tableSettings.activeFilters || {},
      search: this.tableSettings.search,
      archived: this.tableSettings.archived,
      sorting: this.tableSettings.sort
        ? {
          column: this.tableSettings.sort.columnKey,
          order: this.tableSettings.sort.order
        }
        : null
    }, { responseType: 'arraybuffer' }).then(r => {
      const url = window.URL.createObjectURL(new Blob([r.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'Export_Keuringen_' + this.getCurrentDate() + '.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      notification.success({
        message: 'Download gestart.',
        description: 'De download is succesvol gestart. Ga naar je download map om de Excel export te bekijken.',
        duration: 5
      })
    }).catch(() => {
      notification.error({
        message: 'Download NIET gestart.',
        description: 'De download is mislukt. Probeer het later nog een keer.',
        duration: 0
      })
    }).finally(() => {
      this.downloading = false
    })
  }

  getCurrentDate () : string {
    return new Date().toLocaleDateString('nl-NL')
  }

  handleTableChange (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
    // Remove key from filters if filter is an empty object.
    if (filters && Object.keys(filters).length) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key] || !filters[key].length) {
          delete filters[key]

          this.columns.forEach((column:Column) => {
            if (column.key === key) {
              column.filteredValue = null
            }
          })
        }
      })
    }

    // Set pagination, filters and sorting on tableSettings.
    this.tableSettings.pagination = pagination
    this.tableSettings.activeFilters = filters
    this.tableSettings.sort = sort.order ? sort : null

    // Get list based on filters and search term.
    this.reload()
  }

  handleSearchChange () : void {
    // Set page to 1 because of new search but retain filters and sorting.
    this.tableSettings.pagination.current = 1

    // Get list based on filters and search term.
    this.reload()
  }

  // Required for ActiveFilters component
  updateFilters (filters: {[key: string]: number[]|string[]|boolean[]}) : void {
    const { columns } = this

    columns.forEach((column:Column) => {
      column.filteredValue = column.key in filters ? filters[column.key] : []
    })

    this.tableSettings.activeFilters = Object.assign({}, filters)
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  filterVisible = false
  filterDropdownVisibleChange (visible:boolean) : void {
    this.filterVisible = visible
  }

  onRowClick (rowData:{id:number}) : { on: { click: () => void }} {
    return {
      on: {
        click: () => {
          if (!this.selectedRowKeys.length) {
            const keyboard = window.event as KeyboardEvent
            if (can(this.settings.permissionNames.edit)) {
              const route = this.$router.resolve({ name: this.settings.routeNames.edit, params: { [this.settings.routeParamKey]: rowData.id.toString() } })
              window.open(route.href, keyboard.ctrlKey ? '_blank' : '_self')
            } else if (can(this.settings.permissionNames.show)) {
              const route = this.$router.resolve({ name: this.settings.routeNames.show, params: { [this.settings.routeParamKey]: rowData.id.toString() } })
              window.open(route.href, keyboard.ctrlKey ? '_blank' : '_self')
            }
          } else {
            this.selectedRowKeys.includes(rowData.id) ? this.selectedRowKeys.splice(this.selectedRowKeys.indexOf(rowData.id), 1) : this.selectedRowKeys.push(rowData.id)
          }
        }
      }
    }
  }

  onSelectChange (selectedRowKeys:number[]) : void {
    this.selectedRowKeys = selectedRowKeys
  }

  navigateTo (routeName:string, id:number|undefined) : void {
    if (id) {
      this.$router.push({ name: routeName, params: { [this.settings.routeParamKey]: id.toString() } })
    } else {
      this.$router.push({ name: routeName })
    }
  }

  get itemList () : { data: Survey[], meta: { per_page:string, total:number }, filters: { [key:string]: FilterOption[] } } {
    return this.$store.getters[this.settings.getterNames.list]
  }

  get filters () : { [key:string]: FilterOption[] } {
    return this.itemList.filters || {}
  }

  get pagination () : Pagination {
    return {
      current: this.tableSettings.pagination.current,
      defaultPageSize: this.tableSettings.pagination.pageSize,
      pageSize: parseInt(this.itemList.meta.per_page),
      total: this.itemList.meta.total,
      showTotal: (total, range) => `Keuring ${range[0]} t/m ${range[1]} van de ${total}`,
      showSizeChanger: this.tableSettings.showSizeChanger,
      pageSizeOptions: this.tableSettings.pageSizeOptions
    }
  }

  get authUser () : User {
    return this.$store.getters['authStore/authUser']
  }

  get loading () : boolean {
    return this.$store.getters[this.settings.getterNames.loading].list
  }

  columnFilterKeys : { [key:string]: string } = {
    'project.id': 'projects',
    'survey_form.id': 'survey_forms',
    'building_block.name': 'building_blocks',
    'building.number': 'building_numbers',
    'scheduled_user.id': 'scheduled_users',
    status: 'statuses',
    result: 'results'
  }

  @Watch('filters', { immediate: true, deep: true })
  onFiltersChange () : void {
    this.columns.forEach((column:Column) => {
      const filterKey = this.columnFilterKeys[column.key] || null
      if (filterKey && this.filters && filterKey in this.filters) {
        column.filters = this.filters[filterKey]
      }
    })
  }

  @Watch('tableSettings', { deep: true })
  onTableSettingsChange () : void {
    // On tableSettings change, store them in local storage.
    ls.set(this.settings.localStorageName, this.tableSettings)

    axios.post(process.env.VUE_APP_API_URL + '/users/admin/settings', {
      settingName: this.settings.localStorageName,
      settingValue: this.tableSettings
    })

    // Set the current active filters on the columns.
    this.columns.forEach((column:Column) => {
      // Set active filters.
      if (this.tableSettings.activeFilters && column.key in this.tableSettings.activeFilters) {
        column.filteredValue = this.tableSettings.activeFilters[column.key]
      }

      // Set active sort order.
      if (this.tableSettings.sort && column.key === this.tableSettings.sort.columnKey) {
        column.defaultSortOrder = this.tableSettings.sort.order
        column.sortOrder = this.tableSettings.sort.order
      } else {
        column.sortOrder = false
      }
    })
  }
}
